import { DateTime, Interval } from 'luxon';
import { TimeAssessmentFilterEnum } from '@whizz/api-model';
import { FIRST_ASSESSMENT_DATE, LAST_ASSESSMENT_DATE } from './constants';
import { round } from './numbers';

export const YEAR_IN_WEEKS = 52;
export const TODAY = new Date();

export const getDateTimeObj = (date: DateTime | string, localeId: string | undefined): DateTime => {
  let dateObj = (date instanceof DateTime ? date : DateTime.fromISO(date))
    .setLocale(localeId ?? 'en-gb');

  if (localeId === 'th-th') {
    dateObj = dateObj.reconfigure({ outputCalendar: 'buddhist' });
  }

  return dateObj;
};

export const getDateStringWithLocale = (date: Date, locale: string | undefined): string => getDateTimeObj(DateTime.fromJSDate(date), locale).toLocaleString();

export const formatDateForDisplay = (date: string): string => {
  const _d = DateTime.fromISO(date);
  return _d.toFormat('ccc dd LLL yyyy');
};

export const dateTimeToDateString = (dateTime: unknown): unknown | string => {
  if (DateTime.isDateTime(dateTime)) {
    return dateTime.toFormat('y-LL-dd');
  }

  return dateTime;
};

export const toISOFromSCFormat = (date: string): string => {
  return DateTime.fromFormat(date, 'yyyy-LL-dd HH:mm:ss').toISO();
};

export const generateDateTime = (date: string | DateTime | null): DateTime => {
  if (null === date) {
    return DateTime.now();
  }

  if (DateTime.isDateTime(date)) {
    return date;
  }

  if (TimeAssessmentFilterEnum.FIRST_ASSESSMENT === date) {
    return DateTime.fromISO(FIRST_ASSESSMENT_DATE);
  }

  if (TimeAssessmentFilterEnum.LAST_ASSESSMENT === date) {
    return DateTime.fromISO(LAST_ASSESSMENT_DATE);
  }

  return DateTime.fromISO(date);
};

export const getDiffWeeks = (startDate: string | DateTime, endDate: string | DateTime | null): number => {
  const weeks = Interval
    .fromDateTimes(generateDateTime(startDate), generateDateTime(endDate))
    .length('weeks');

  return Math.floor(weeks);
};

/**
 * @param weekday (1-7)
 */
export function getPrevWeekday(weekday: number, hour: number, fromDate: DateTime = DateTime.now()): DateTime {
  const finalDate = fromDate.set({ hour: hour, minute: 0, second: 0, millisecond: 0 });

  if (weekday === fromDate.weekday && hour > (fromDate.hour + fromDate.minute / 60)) {
    return finalDate.minus({ days: 7 });
  }

  if (fromDate.weekday > weekday) {
    return finalDate.minus({ days: fromDate.weekday - weekday });
  }

  return finalDate.minus({ days: 7 - weekday + fromDate.weekday });
}

/**
 * @param weekday (1-7)
 */
export function getNextWeekday(weekday: number, hour: number, fromDate: DateTime = DateTime.now()): DateTime {
  const finalDate = fromDate.set({ hour: hour, minute: 0, second: 0, millisecond: 0 });

  if (weekday === fromDate.weekday && hour < (fromDate.hour + fromDate.minute / 60)) {
    return finalDate.plus({ days: 7 });
  }

  return finalDate.plus({ days: (7 - fromDate.weekday + weekday) % 7 });
}

export const getMondaysSinceDate = (date: DateTime): number => {
  const pastDate = date.endOf('week');
  const today = DateTime.now().startOf('day');

  let count = 0;
  let current = pastDate;

  while (current <= today) {
    if (current.weekday === 1) {
      count++;
    }
    current = current.plus({ days: 1 });
  }
  return count;
};

export function timezoneWeekdayAndHourToUtc(weekday: number, hour: number, timezone: string): HourAndWeekDayToUtcReturn {
  const schoolLocalDatetime = DateTime.now().setZone(timezone);
  const isSchoolInDst = schoolLocalDatetime.isInDST;

  const schoolLocalNextWeekday = getNextWeekday(weekday, hour, schoolLocalDatetime);
  let utcNextWeekday = schoolLocalNextWeekday.toUTC();

  if (isSchoolInDst) {
    utcNextWeekday = utcNextWeekday.plus({ hour: 1 });
  }

  return {
    weekday: utcNextWeekday.weekday,
    hour: utcNextWeekday.hour,
  };
}

export function utcWeekdayAndHourToTimeZone(weekday: number, hour: number, timezone: string): HourAndWeekDayToUtcReturn {
  const isSchoolInDst = DateTime.now().setZone(timezone).isInDST;

  const utcDatetime = DateTime.now().toUTC();

  const utcNextWeekday = getNextWeekday(weekday, hour, utcDatetime);
  let schoolLocalNextWeekday = utcNextWeekday.setZone(timezone);

  if (isSchoolInDst) {
    schoolLocalNextWeekday = schoolLocalNextWeekday.minus({ hour: 1 });
  }

  return {
    weekday: schoolLocalNextWeekday.weekday,
    hour: schoolLocalNextWeekday.hour,
  };
}

export const yearAsDecimalToWeeks = (years: number): number => round(years * 52, 1);
export const convertYearsToWeeks = (value: number): number => round(value * YEAR_IN_WEEKS, 1);
export const convertWeeksToYears = (value: number): number => round(value / YEAR_IN_WEEKS, 2);

interface HourAndWeekDayToUtcReturn {
  weekday: number;
  hour: number;
}

export type DurationUnit = 'unitWeek' | 'unitYear';
